import { type ElementType, type ReactNode } from 'react';
import styles from './page-shell.module.css';

/**
 * Üç ray — devir paketi README §4.0. Sayfa kendi kabını TANIMLAMAZ, yalnızca
 * hangi rayda olduğunu bildirir.
 */
export type PageRail = 'app' | 'reading' | 'public' | 'site';

/**
 * İçerik sütunu — RAY DEĞİL. Ray sayfanın kabıdır; sütun o kabın içindeki bir
 * kolondur (sözleşme 232 yan menü, 240 ayarlar menüsü, 344/420 detay paneli
 * gibi sütunları zaten tanır). Dördüncü bir ray AÇILMADI.
 */
export type PageColumn = 'narrow' | 'siteForm' | 'siteDocument' | 'siteArticle';

export interface PageShellProps {
  /**
   * `app` → web'de 1424 (içerik 1360, R turu 29.10.2026), ortalı (veri yoğun yüzeyler).
   * `reading` → 880, uygulama rayının İÇİNDE ORTALI (tek sütunlu okuma/form).
   * `public` → web'de 1424, ortalı (public etkinlik/konuşmacı, kartvizit, landing).
   * `site` → web'de 1360 (içerik 1280, kenar xl ve üstünde 40): misafir site
   * kabuğu (ana sayfa, Bize ulaşın, yasal; kartlarda 1440'ta 80 px kenar).
   * Değerler `--ib-rail-*` değişkenlerinden gelir (`web-tema/yerlesim.ts`).
   */
  readonly rail: PageRail;
  /**
   * Rayın İÇİNDEKİ dar içerik sütunu (§8.4 #29/#30 — davet/başvuru ekranları).
   * Ray gibi ortalıdır; verilmezse içerik rayın tamamını kullanır.
   * `siteForm` (1100) ve `siteDocument` (1200): site kabuğunun ortalı iç
   * sütunları (SiteBizeUlasin · SiteYasal kartlarının `main` kutusu).
   * `siteArticle` (720): SiteYasal metin sütununun okuma genişliği; tek
   * sütunlu metin sayfası (SiteSSS) bunu kendi `main` kutusu olarak kullanır.
   */
  readonly column?: PageColumn;
  /** Sayfanın kendi düzen sınıfı — İÇ kutuya biner (display/gap/padding-block). */
  readonly className?: string;
  /**
   * Dış kutunun etiketi (ör. `section`). Web'de `main` verilmez: kök düzen
   * zaten `main` çizer (`ic-ice-main.guard.spec.ts`).
   */
  readonly as?: ElementType;
  readonly id?: string;
  /** Yalnız kabın KENDİSİ bir canlı bölge/işaret olduğunda (ör. hata sınırı). */
  readonly role?: string;
  readonly children: ReactNode;
}

/**
 * TEK SAYFA KABI. Otuz yüzey eskiden kendi kabını ayrı ayrı tanımlıyordu
 * (1440 · 1180 · 952 · sınırsız, dolgu 16–28px); ayrışmanın kök nedeni kabın
 * bir bileşen değil 30 ayrı CSS kuralı olmasıydı. Kap artık burada.
 *
 * Bileşen yalnız ÜÇ şeyi sahiplenir: `max-width`, `margin-inline`,
 * `padding-inline`. Dikey ritim, ızgara ve `gap` sayfanın kendi sınıfında
 * kalır — `className` iç kutuya geçirilir.
 *
 * Kalıcı kontrol: `page-shell-rail.guard.spec.ts` depoyu `git ls-files` ile
 * tarar; bir sayfa `PageShell` kullanmadan kendi ray genişliğini tanımlarsa
 * test KIRILIR.
 */
export function PageShell({ rail, column, className, as, id, role, children }: PageShellProps) {
  const Outer: ElementType = as ?? 'div';
  const inner = [
    styles.inner,
    rail === 'reading' ? styles.innerReading : '',
    column === 'narrow' ? styles.innerNarrow : '',
    column === 'siteForm' ? styles.innerSiteForm : '',
    column === 'siteDocument' ? styles.innerSiteDocument : '',
    column === 'siteArticle' ? styles.innerSiteArticle : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Outer
      className={[styles.outer, styles[rail]].join(' ')}
      data-rail={rail}
      data-column={column}
      id={id}
      role={role}
    >
      <div className={inner}>{children}</div>
    </Outer>
  );
}
